import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { formatDuration } from '@/lib/format';
import { cn } from '@/lib/utils';

export type ExamTimerProps = {
  remainingSeconds: number | null;
};

/** ห้ามสื่อสถานะด้วยสีอย่างเดียว — มีทั้งไอคอน ข้อความ และการประกาศผ่าน aria-live */
export function ExamTimerDisplay({ remainingSeconds }: ExamTimerProps) {
  const { t } = useTranslation();
  const [announcement, setAnnouncement] = useState('');
  const lastAnnounced = useRef<number | null>(null);

  useEffect(() => {
    if (remainingSeconds === null) return;
    const minutes = Math.ceil(remainingSeconds / 60);
    // ประกาศทุก 5 นาทีสำหรับผู้ใช้ screen reader
    if (minutes > 0 && minutes % 5 === 0 && lastAnnounced.current !== minutes) {
      lastAnnounced.current = minutes;
      setAnnouncement(t('exam.timeAnnouncement', { minutes }));
    }
  }, [remainingSeconds, t]);

  if (remainingSeconds === null) return null;

  const urgent = remainingSeconds <= 60;
  const warning = !urgent && remainingSeconds <= 300;

  return (
    <div className="flex items-center gap-1.5">
      <Clock
        aria-hidden
        className={cn(
          'h-4 w-4',
          urgent ? 'text-red-700' : warning ? 'text-amber-700' : 'text-slate-500',
        )}
      />
      <span className="sr-only">{t('exam.timeLeft')}:</span>
      <span
        className={cn(
          'font-mono text-base font-semibold tabular-nums',
          urgent ? 'animate-pulse text-red-700' : warning ? 'text-amber-700' : 'text-slate-900',
        )}
      >
        {formatDuration(remainingSeconds)}
      </span>
      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
}
